import React, { useEffect, useMemo, useState } from "react";
import { FaGraduationCap, FaListUl, FaCheckCircle, FaBook, FaHistory, FaChartBar, FaHourglassHalf, FaTimesCircle, FaAward, FaArrowLeft } from "react-icons/fa";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import usePageTitle from "../hooks/usePageTitle";

/**
 * Education — Navy/Black/Emerald Glass UI (Hash routing)
 * Routes:
 *   #/                → List page
 *   #/quiz/:quizId   → Quiz page
 */

const API_BASE = "https://dev.sliitleo.org/Backend";

/* -------- Utilities -------- */
function crc32(str) {
  let crc = 0 ^ (-1);
  for (let i = 0; i < str.length; i++) crc = (crc >>> 8) ^ CRC_TABLE[(crc ^ str.charCodeAt(i)) & 0xff];
  return (crc ^ (-1)) >>> 0;
}
const CRC_TABLE = (() => {
  let c, table = new Array(256);
  for (let n = 0; n < 256; n++) {
    c = n; for (let k = 0; k < 8; k++) c = ((c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1));
    table[n] = c >>> 0;
  }
  return table;
})();
const quizIdInt = (subject, quiz) => crc32(`${String(subject||"").trim()}||${String(quiz||"").trim()}`);

function useHashRoute() {
  const [hash, setHash] = useState(() => window.location.hash || "#/");
  useEffect(() => { const on = () => setHash(window.location.hash || "#/"); window.addEventListener("hashchange", on); return () => window.removeEventListener("hashchange", on); }, []);
  return hash;
}

/* ================================= Component ================================= */
export default function Education() {
  /* Page title */
  const hash = useHashRoute();
  const route = useMemo(() => {
    const m = hash.match(/^#\/quiz\/(\d+)/);
    if (m) return { name: "quiz", quizId: parseInt(m[1], 10) };
    return { name: "home" };
  }, [hash]);
  usePageTitle(route.name === "quiz" ? "Take Quiz" : "Education — Quizzes");

  /* Theme tokens: refined + professional */
  const C = {
    // palette
    navy0: "#060b18",
    navy1: "#081226",
    navy2: "#0a1630",
    navy3: "#0e213f",
    ink:   "#0b0f1a",
    white: "#f5f7fb",
    sub:   "#cbd6f6",
    blue:  "#1f6fff",
    green: "#10b981",
    red:   "#ef4444",
    amber: "#f59e0b",
    // surfaces
    glass: "rgba(255,255,255,0.045)",
    border: "rgba(255,255,255,0.12)",
    borderSoft: "rgba(255,255,255,0.08)",
  };

  /* State */
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");
  const [completedIds, setCompletedIds] = useState([]); // int[]
  const [quizScores, setQuizScores] = useState({});      // { [id:number]: number }
  const [totalScore, setTotalScore] = useState(null);

  const [userAnswers, setUserAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [quizStartTime, setQuizStartTime] = useState(null);
  const [quizEndTime, setQuizEndTime] = useState(null);

  const user = (() => {
    try { return JSON.parse(localStorage.getItem("user")) || { name: "Student", role: "SLIIT Student", image: "/default-user.png" }; }
    catch { return { name: "Student", role: "SLIIT Student", image: "/default-user.png" }; }
  })();
  const sliitid = localStorage.getItem("sliitid") || "";

  /* Group questions → subject+quiz */
  const subjectQuizMap = {};
  questions.forEach(q => {
    const key = `${q.subject}||${q.quiz}`;
    if (!subjectQuizMap[key]) {
      const id = quizIdInt(q.subject, q.quiz);
      subjectQuizMap[key] = { subject: q.subject, quiz: q.quiz, quiz_id: id, questions: [], maxId: -1 };
    }
    subjectQuizMap[key].questions.push(q);
    const n = parseInt(q.id, 10);
    if (!isNaN(n) && n > subjectQuizMap[key].maxId) subjectQuizMap[key].maxId = n;
  });
  const subjectQuizList = Object.values(subjectQuizMap);
  const newUpdated = [...subjectQuizList].sort((a, b) => (b.maxId - a.maxId)).slice(0, 6);

  const totalQuizzes = subjectQuizList.length;
  const completedCount = subjectQuizList.filter(qz => completedIds.includes(qz.quiz_id)).length;

  /* Load data */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true); setLoadErr("");
        const q = await fetch(`${API_BASE}/get_questions.php`);
        const qd = await q.json();
        if (!Array.isArray(qd?.questions)) throw new Error("Invalid questions format");
        if (!cancelled) setQuestions(qd.questions);

        if (sliitid) {
          const c = await fetch(`${API_BASE}/get_completed_quizzes.php`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ sliitid })
          });
          const cd = await c.json();
          if (cd?.success) {
            if (!cancelled && Array.isArray(cd.completed_quiz_ids)) setCompletedIds(cd.completed_quiz_ids);
            if (!cancelled && cd.quiz_scores && typeof cd.quiz_scores === "object") setQuizScores(cd.quiz_scores);
            if (!cancelled && typeof cd.total_score === "number") setTotalScore(cd.total_score);
          }
        }
      } catch (e) {
        if (!cancelled) setLoadErr("Failed to load. Please try again.");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Routing helpers */
  const gotoHome = () => (window.location.hash = "#/");
  const gotoQuiz = (id) => (window.location.hash = `#/quiz/${id}`);

  /* Scoring */
  const calcScore = (qs, ans) => {
    if (!Array.isArray(qs) || !ans) return 0;
    return qs.reduce((acc, q) => {
      const correctIdx = parseInt(q.correct_option, 10) - 1;
      return acc + ((ans[q.id] === correctIdx) ? 10 : 0);
    }, 0);
  };
  const calcScoreWithTime = (qs, ans, timeTaken, free = 30, step = 60) => {
    const raw = calcScore(qs, ans);
    if (typeof timeTaken !== "number" || timeTaken <= free) return raw;
    const penalty = Math.floor((timeTaken - free) / step) * 10;
    return Math.max(0, raw - penalty);
  };

  /* Current quiz */
  const currentQuiz = useMemo(() => {
    if (route.name !== "quiz") return null;
    return subjectQuizList.find(q => q.quiz_id === route.quizId) || null;
  }, [route, subjectQuizList]);

  /* Reset on entering quiz */
  useEffect(() => {
    if (route.name === "quiz") {
      setUserAnswers({}); setShowResults(false); setQuizStartTime(Date.now()); setQuizEndTime(null);
    }
  }, [route.name]);

  /* Styles (tight, professional) */
  const radiusL = "18px";
  const radiusM = "14px";
  const radiusS = "10px";
  const shadowSoft = "0 20px 60px -35px rgba(0,0,0,.7)";
  const innerGlass = `inset 0 1px 0 rgba(255,255,255,.06)`;

  return (
    <div className="edu-app">
      <style>{`
        :root{
          --navy0:${C.navy0}; --navy1:${C.navy1}; --navy2:${C.navy2}; --navy3:${C.navy3}; --ink:${C.ink};
          --white:${C.white}; --sub:${C.sub}; --blue:${C.blue}; --green:${C.green}; --red:${C.red}; --amber:${C.amber};
          --glass:${C.glass}; --border:${C.border}; --borderSoft:${C.borderSoft};
        }
        .edu-app{
          display:flex; min-height:100vh; color:var(--white); overflow-x:hidden; position:relative;
          font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Inter, "SF Pro Text", sans-serif;
          background:
            radial-gradient(1400px 900px at 10% -10%, #0e2a6a33 0%, transparent 60%),
            radial-gradient(1400px 900px at 120% 10%, #0a3b5a33 0%, transparent 60%),
            conic-gradient(from 200deg at 80% -10%, #0d1b3a 0deg, #091226 60deg, #0a1327 180deg, #07101f 300deg),
            linear-gradient(180deg, var(--ink), var(--navy1) 60%, var(--navy0));
        }
        .main{ width:100%; box-sizing:border-box; padding:88px 18px 80px; }
        @media (min-width:1024px){ .main{ padding:88px 56px 90px 320px; } }

        /* Top hero */
        .hero{ background:var(--glass); border:1px solid var(--border); border-radius:${radiusL}; padding:16px 18px; backdrop-filter: blur(12px);
               box-shadow:${shadowSoft}, ${innerGlass}; margin-bottom:14px; }
        .hero .row{ display:flex; align-items:center; justify-content:space-between; gap:10px; }
        .badge{ display:grid; place-items:center; width:46px; height:46px; border-radius:${radiusM};
                background: linear-gradient(135deg, #0e1f47, #0b1938); border:1px solid var(--borderSoft); }
        .heroTitle{ font-size:16px; font-weight:800; letter-spacing:.2px; opacity:.96; }

        /* Stats (compact) */
        .stats{ display:grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap:10px; margin-top:12px; }
        @media (min-width:900px){ .stats{ grid-template-columns: repeat(5, minmax(0,1fr)); } }
        .stat{ display:flex; gap:10px; align-items:center; background: rgba(255,255,255,.04);
               border:1px solid var(--borderSoft); border-radius:${radiusM}; padding:10px 12px; }
        .stat i{ width:30px; height:30px; display:grid; place-items:center; border-radius:${radiusS};
                 background: linear-gradient(135deg, #0c1e43, #0a1733); border:1px solid var(--borderSoft); }
        .stat h4{ margin:0; font-size:12px; font-weight:800; color:var(--sub); }
        .stat p{ margin:1px 0 0; font-size:12px; font-weight:800; color:#eaf0ff; }

        /* Rail (new & updated) */
        .rail{ margin:14px 0 10px; background: rgba(255,255,255,.04); border:1px solid var(--border);
               border-radius:${radiusL}; padding:12px; box-shadow:${innerGlass}; }
        .railHead{ display:flex; align-items:center; justify-content:space-between; margin-bottom:8px; }
        .railTitle{ font-size:13px; font-weight:800; color:#e9efff; }
        .railLine{ display:flex; gap:8px; overflow:auto; scrollbar-width:none; padding-bottom:4px; }
        .railLine::-webkit-scrollbar{ display:none; }
        .pill{ white-space:nowrap; background: rgba(255,255,255,.05); border:1px solid var(--borderSoft); color:#eaf2ff;
               border-radius:999px; padding:6px 10px; font-weight:800; font-size:11px; display:inline-flex; gap:8px; align-items:center; }
        .dot{ width:7px; height:7px; border-radius:50%; background: linear-gradient(90deg, var(--green), #3fd3a7); box-shadow:0 0 0 2px #18c39a33; }

        /* Panels + lists */
        .panel{ background: rgba(255,255,255,.04); border:1px solid var(--border); border-radius:${radiusL}; overflow:hidden; box-shadow:${shadowSoft}, ${innerGlass}; }
        .panelHead{ display:flex; align-items:center; justify-content:space-between; padding:12px 14px;
                    border-bottom:1px solid var(--borderSoft); }
        .panelTitle{ font-size:13px; font-weight:800; }
        .tag{ background: rgba(255,255,255,.05); border:1px solid var(--borderSoft); color:#dfe8ff; padding:5px 9px; border-radius:999px; font-size:11px; font-weight:800; }
        .list{ display:flex; flex-direction:column; }
        .row{ display:grid; grid-template-columns:1fr auto; gap:12px; padding:12px 14px;
              background: linear-gradient(180deg, var(--navy2), var(--navy1)); border-bottom:1px solid var(--borderSoft); }
        .row:last-child{ border-bottom:none; }
        .meta{ display:flex; gap:8px; align-items:center; flex-wrap:wrap; color:var(--sub); font-weight:800; font-size:11px; margin-top:6px; }
        .progress{ height:7px; background: #08142c; border:1px solid #14264a; border-radius:999px; overflow:hidden; margin-top:8px; position:relative; }
        .progress>span{ position:absolute; inset:0 auto 0 0; width:0%; background: linear-gradient(90deg, var(--green), #3fd3a7); box-shadow:0 0 14px -6px var(--green); }

        /* Buttons */
        .btn{ display:inline-flex; gap:8px; align-items:center; border-radius:${radiusM}; border:1px solid rgba(255,255,255,.14); padding:9px 12px;
              font-weight:800; color:#fff; background: rgba(255,255,255,.06); transition: transform .06s ease, filter .2s ease; }
        .btn:hover{ filter:brightness(1.05); }
        .btn:active{ transform: translateY(1px); }
        .btn.primary{ background: linear-gradient(90deg, var(--green), #14a87f); box-shadow: 0 10px 22px -10px #10b98177; border:1px solid #14b98a66; }
        .btn.muted{ opacity:.65; cursor:not-allowed; }

        /* Quiz page */
        .sub{ display:flex; align-items:center; justify-content:space-between; gap:10px; background: rgba(255,255,255,.05); border:1px solid var(--border);
              border-radius:${radiusM}; padding:10px 12px; margin: 8px 0 10px; }
        .q{ background: linear-gradient(180deg, var(--navy2), var(--navy1)); border:1px solid #152a53; border-radius:${radiusM}; padding:14px; margin-bottom:10px; box-shadow:${shadowSoft}; }
        .qTitle{ font-weight:800; margin-bottom:10px; font-size:13px; }
        .opt{ display:flex; gap:10px; align-items:center; padding:10px 12px; background:var(--navy0); border:1px solid #213a73; border-radius:${radiusM}; color:#eaf2ff; font-weight:800; cursor:pointer; transition:all .12s ease; }
        .opt:hover{ border-color:#2b4c9f; }
        .opt.active{ background:#0f2147; border-color:#2b4c9f; }
        .opt.correct{ border-color:var(--green); box-shadow: inset 0 0 0 2px #10b98155; }
        .opt.wrong{ border-color:var(--red); box-shadow: inset 0 0 0 2px #ef444455; }
        .bullet{ width:14px; height:14px; border-radius:50%; border:2px solid #7e8db6; flex:0 0 auto; }
        .opt.active .bullet{ border-color:#2b4c9f; background:#2b4c9f; }
        .opt.correct .bullet{ border-color:var(--green); background:var(--green); }
        .opt.wrong .bullet{ border-color:var(--red); background:var(--red); }

        .result{ margin-top:10px; padding:14px; border-radius:${radiusM};
                 background: linear-gradient(135deg, #08152f, #0a1a39); border:1px solid #17c29655; box-shadow: 0 22px 60px -36px #10b98155; }

        /* Center/feedback */
        .center{ text-align:center; padding:60px 0; color:#eaf2ff; font-weight:800; font-size:13px; }
        .spin{ animation: spin 2s linear infinite; } @keyframes spin{ from{transform:rotate(0)} to{transform:rotate(360deg)} }

        /* Titles (compact) */
        .titleLine{ display:flex; align-items:center; gap:10px; }
        .titleStrong{ font-weight:800; font-size:14px; letter-spacing:.2px; }
        .titleSub{ color:var(--sub); }
      `}</style>

      <Sidebar />
      <Topbar user={user} />

      <main className="main">
        {route.name === "home" ? (
          <>
            {/* HERO */}
            <div className="hero">
              <div className="row">
                <div className="titleLine">
                  <div className="badge"><FaGraduationCap size={18} /></div>
                  <div className="titleStrong">Education · Quizzes</div>
                </div>
              </div>
              <div className="stats">
                <div className="stat"><i><FaListUl /></i><div><h4>Total Quizzes</h4><p>{totalQuizzes}</p></div></div>
                <div className="stat"><i><FaCheckCircle color={C.green} /></i><div><h4>Completed</h4><p>{completedCount}</p></div></div>
                <div className="stat"><i><FaBook color={C.blue} /></i><div><h4>In Progress</h4><p>0</p></div></div>
                <div className="stat"><i><FaChartBar color={C.green} /></i><div><h4>Total Score</h4><p>{totalScore ?? "—"}</p></div></div>
                <div className="stat"><i><FaHistory color={C.amber} /></i><div><h4>Last Updated</h4><p>{new Date().toLocaleDateString()}</p></div></div>
              </div>
            </div>

            {/* New & Updated */}
            <div className="rail">
              <div className="railHead"><div className="railTitle">New & Updated</div></div>
              <div className="railLine">
                {newUpdated.map(it => (
                  <span className="pill" key={`${it.subject}-${it.quiz}`}><span className="dot" /> {it.subject}: {it.quiz}</span>
                ))}
              </div>
            </div>

            {/* All Quizzes */}
            <div className="panel">
              <div className="panelHead"><div className="panelTitle">All Quizzes</div><div className="tag">{totalQuizzes} total</div></div>
              {loading ? (
                <div className="center"><FaHourglassHalf className="spin" style={{marginRight:8}} /> Loading…</div>
              ) : loadErr ? (
                <div className="center" style={{color:C.red}}><FaTimesCircle style={{marginRight:8}} /> {loadErr}</div>
              ) : (
                <div className="list">
                  {subjectQuizList
                    .filter(sq => !completedIds.includes(sq.quiz_id))
                    .map((sq) => {
                      const qCount = sq.questions.length;
                      return (
                        <div className="row" key={`${sq.subject}-${sq.quiz}`}>
                          <div>
                            <div className="titleStrong">
                              {sq.subject} <span className="titleSub">/ {sq.quiz}</span>
                            </div>
                            <div className="meta">
                              <span className="tag">{qCount} {qCount === 1 ? "Question" : "Questions"}</span>
                              <span className="tag">Est. 5–10 min</span>
                            </div>
                            <div className="progress"><span style={{width:"0%"}} /></div>
                          </div>
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <button className="btn primary" onClick={() => gotoQuiz(sq.quiz_id)}>Start</button>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>

            {/* Completed */}
            <div className="panel" style={{marginTop:12}}>
              <div className="panelHead"><div className="panelTitle">Completed</div><div className="tag">{completedCount} done</div></div>
              <div className="list">
                {subjectQuizList.filter(sq => completedIds.includes(sq.quiz_id)).length === 0 ? (
                  <div className="row"><div><div className="titleStrong" style={{opacity:.9}}>No completed quizzes yet</div><div className="meta">Finish a quiz to see it here.</div></div></div>
                ) : (
                  subjectQuizList
                    .filter(sq => completedIds.includes(sq.quiz_id))
                    .map(sq => {
                      const qCount = sq.questions.length;
                      const score = typeof quizScores[sq.quiz_id] === "number" ? quizScores[sq.quiz_id] : null;
                      return (
                        <div className="row" key={`completed-${sq.subject}-${sq.quiz}`}>
                          <div>
                            <div className="titleStrong">
                              {sq.subject} <span className="titleSub">/ {sq.quiz}</span>
                            </div>
                            <div className="meta">
                              <span className="tag">{qCount} {qCount === 1 ? "Question" : "Questions"}</span>
                              <span className="tag" style={{background:"rgba(16,185,129,.18)", borderColor:"#10b98155", color:"#c8ffef"}}>
                                <FaCheckCircle style={{marginRight:6}}/> Completed
                              </span>
                              {score !== null && <span className="tag">Score: {score}</span>}
                            </div>
                            <div className="progress"><span style={{width:"100%"}} /></div>
                          </div>
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <div className="btn muted">Done</div>
                          </div>
                        </div>
                      );
                    })
                )}
              </div>
            </div>
          </>
        ) : route.name === "quiz" && currentQuiz ? (
          <>
            {/* Quiz Header */}
            <div className="hero" style={{marginBottom:10}}>
              <div className="row">
                <div className="titleLine">
                  <div className="badge"><FaBook size={16}/></div>
                  <div className="titleStrong">Quiz · {currentQuiz.subject} <span className="titleSub">/ {currentQuiz.quiz}</span></div>
                </div>
                <button className="btn" onClick={gotoHome}><FaArrowLeft/> Back</button>
              </div>
            </div>

            <div className="sub">
              <div style={{fontWeight:800, fontSize:12}}>Answer the questions and submit.</div>
              <div className="tag">Questions: {currentQuiz.questions.length}</div>
            </div>

            {/* Quiz form */}
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setShowResults(true);
                const endTime = Date.now();
                setQuizEndTime(endTime);

                const t = quizStartTime ? Math.floor((endTime - quizStartTime) / 1000) : 0;
                const finalScore = calcScoreWithTime(currentQuiz.questions, userAnswers, t);

                let name = "";
                try { const u = JSON.parse(localStorage.getItem("user")); name = (u && u.name) ? u.name : ""; } catch {}

                try {
                  const r = await fetch(`${API_BASE}/save_quiz_score.php`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ sliitid, name, score: finalScore, quizId: currentQuiz.quiz_id })
                  });
                  const d = await r.json();
                  if (d?.success) {
                    if (Array.isArray(d.completed_quiz_ids)) setCompletedIds(d.completed_quiz_ids);
                    if (d.quiz_scores && typeof d.quiz_scores === "object") setQuizScores(d.quiz_scores);
                    if (typeof d.total_score === "number") setTotalScore(d.total_score);
                  } else {
                    console.warn("Failed to save score:", d?.error);
                  }
                } catch (err) {
                  console.warn("Error saving score:", err);
                }
              }}
            >
              {currentQuiz.questions.map((q, idx) => {
                const correctIdx = parseInt(q.correct_option, 10) - 1;
                const chosen = userAnswers[q.id];
                return (
                  <div className="q" key={q.id}>
                    <div className="qTitle">Q{idx + 1}. {q.question}</div>
                    <div style={{display:"grid", gap:8}}>
                      {[q.option1, q.option2, q.option3, q.option4].map((opt, optIdx) => {
                        const active = chosen === optIdx;
                        const stateClass = showResults
                          ? (optIdx === correctIdx ? "correct" : (active ? "wrong" : ""))
                          : (active ? "active" : "");
                        return (
                          <label
                            key={optIdx}
                            className={`opt ${stateClass}`}
                            onClick={() => !showResults && setUserAnswers(prev => ({ ...prev, [q.id]: optIdx }))}
                          >
                            <span className="bullet" />
                            <input type="radio" name={`q${q.id}`} checked={active} onChange={() => {}} disabled={showResults} style={{display:"none"}} />
                            <span style={{flex:1, fontSize:13}}>{opt}</span>
                            {showResults && optIdx === correctIdx && <FaCheckCircle color={C.green}/>}
                            {showResults && active && optIdx !== correctIdx && <FaTimesCircle color={C.red}/>}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {!showResults ? (
                <button type="submit" className="btn primary" style={{width:220, marginTop:6}}>
                  <FaAward/> Submit
                </button>
              ) : (
                <div className="result">
                  {(() => {
                    const t = quizEndTime && quizStartTime ? Math.floor((quizEndTime - quizStartTime) / 1000) : 0;
                    const scoreWithTime = calcScoreWithTime(currentQuiz.questions, userAnswers, t);
                    const raw = calcScore(currentQuiz.questions, userAnswers);
                    return (
                      <div>
                        <div style={{fontSize:14, fontWeight:800, marginBottom:6}}>
                          <FaAward style={{marginRight:8}}/>
                          Final Score: <span style={{color:C.green}}>{scoreWithTime} / {currentQuiz.questions.length * 10}</span>
                        </div>
                        <div style={{color:"#eaf2ff", fontWeight:800, fontSize:12}}>
                          <span style={{marginRight:10}}>Raw: {raw}</span> |
                          <span style={{marginLeft:10}}>Time: {t}s</span>
                          {t > 30 && <span style={{color:C.amber, marginLeft:10}}>(Time Penalty)</span>}
                        </div>
                        <div style={{marginTop:10}}>
                          <button className="btn" type="button" onClick={gotoHome}><FaArrowLeft/> Back to All Quizzes</button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </form>
          </>
        ) : (
          <div className="center">Quiz not found.</div>
        )}
      </main>
    </div>
  );
}
